<template>
	<component
		:is="displayResponsive ? 'v-card' : 'v-navigation-drawer'"
		v-model="isExpanded"
		:width="displayResponsive ? '100%' : navigation.width"
		:height="displayResponsive ? 'calc(100svh - 55px)' : '100vh;'"
		location="left"
		permanent
		style="background-color: rgb(var(--v-theme-contained))"
	>
		<TopBar />
		<v-spacer />
		<div v-if="!displayResponsive" id="alternisborder"></div>
		<div :style="{ position: 'absolute', right: '-24px', top: 'calc(50% - 24px)' }">
			<v-btn
				:icon="isExpanded ? 'mdi-chevron-left' : 'mdi-chevron-right'"
				style="border-radius: 0 10px 10px 0; width: 24px; height: 60px"
				@click="toggleExpand()"
			></v-btn>
		</div>
		<div
			style="display: flex; flex-direction: column; height: calc(100vh - 50px)"
			:style="displayResponsive ? 'padding-bottom: 150px' : ''"
		>
			<v-container v-scroll.self="onScroll" ref="alternisScrollContainer" style="overflow-y: auto; flex: 1">
				<div v-for="entry in timelineEntries" :key="entry.id">
					<Message
						v-if="entry.kind === 'message' && !hasPrefabPrefix(entry.message) && entry.message.text !== ''"
						:message="entry.message"
					/>
					<div v-else-if="entry.kind === 'toolCall'" class="tool-call-chip-wrapper">
						<v-chip size="small" variant="outlined" class="tool-call-chip">
							<span v-html="formatToolCall(entry.event)"></span>
						</v-chip>
					</div>
				</div>
				<div style="margin-bottom: 16px">
					<BotSSEMessage />
				</div>
				<v-btn
					v-if="alternisNewStore.isExpanded && showFollowHint"
					min-width="260"
					:model-value="showFollowHint"
					style="position: sticky; bottom: 0; margin-left: 70px; cursor: pointer; z-index: 9999"
					rounded="pill"
					@click="() => { following = true; scrollToBottom(); }"
				>
					<v-icon>mdi-chevron-down</v-icon>
					{{ symbolsStore.GUILabel("2283e094-8000-4fd0-a21d-c42c36cb3fc5") }}
				</v-btn>
			</v-container>
			<BottomBar id="bottomBar" />
		</div>
	</component>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, watch, nextTick } from "vue";
import { useSymbolsStore } from "../../../../store/symbols";
import { AlternisMessage, useAlternisNewStore } from "../../../../store/alternis_new";
import { useResponsiveLayout } from "@lr-notitia/notitia-ui";
import { useGraphStore } from "../../../../store/graph";
import { useDomainStore } from "../../../../store/domain";

import TopBar from "./TopBar.vue";
import Message from "./Message.vue";
import BottomBar from "./BottomBar.vue";
import BotSSEMessage from "./BotSSEMessage.vue";
import { type ToolCallEvent, useVoiceBotStore } from "../../../../store/voiceBot";

const alternisNewStore = useAlternisNewStore();
const symbolsStore = useSymbolsStore();
const voiceBotStore = useVoiceBotStore();
const graphStore = useGraphStore();
const domainStore = useDomainStore();

const { displayResponsive } = useResponsiveLayout();

const emits = defineEmits(["change", "createPerson", "createAnnotation"]);

const alternisScrollContainer = ref<any>(null);
const isExpanded = computed(() => alternisNewStore.isExpanded);
const navigation = ref({ minWidth: 300, width: 300 });

const following = ref(true);
const programmaticScroll = ref(false);
const showFollowHint = computed(() => !following.value);

const SCROLL_THRESHOLD = 50; // px tolerance for "at bottom" detection

const sortedMessages = computed(() => {
	return (
		alternisNewStore.messages
			// .concat(notificationMessages.value)
			.sort((a, b) => a.created - b.created)
	);
});

type TimelineEntry =
	| { kind: "message"; id: string; created: number; message: AlternisMessage }
	| { kind: "toolCall"; id: string; created: number; event: ToolCallEvent };

const timelineEntries = computed<TimelineEntry[]>(() => {
	const messageEntries = alternisNewStore.messages.map((message) => ({
		kind: "message" as const,
		id: `message-${message.id}`,
		created: message.created * 1000,
		message,
	}));
	const toolEntries = voiceBotStore.toolCallEvents.map((event) => ({
		kind: "toolCall" as const,
		id: `tool-${event.id}`,
		created: Math.floor(event.receivedAt / 1000) * 1000, // fixes wrong ordering bug since alternis rounds to seconds
		event,
	}));
	return [...toolEntries, ...messageEntries].sort((a, b) => a.created - b.created);
});

function onScroll(e: any) {
	// Ignore scroll events triggered by programmatic scrolling
	if (programmaticScroll.value) return;

	const { scrollTop, clientHeight, scrollHeight } = e.target;
	if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD) {
		following.value = true;
	} else {
		following.value = false;
	}
}

function scrollToBottom() {
	if (!following.value || !alternisScrollContainer.value) return;
	nextTick(() => {
		// Use requestAnimationFrame to ensure the browser has completed layout
		requestAnimationFrame(() => {
			const el = alternisScrollContainer.value?.$el;
			if (!el) return;
			programmaticScroll.value = true;
			el.scrollTop = el.scrollHeight;
			// Reset the flag after the scroll event has fired
			requestAnimationFrame(() => {
				programmaticScroll.value = false;
			});
		});
	});
}

watch(
	() => timelineEntries.value.length,
	() => {
		scrollToBottom();
	},
);

// Also watch message content changes (e.g. SSE replacing the array with updated texts)
watch(
	() => alternisNewStore.messages.map((m) => m.text).join("\0"),
	() => {
		scrollToBottom();
	},
);

function setEvents() {
	const el = document.getElementById("alternisborder");
	let isDragged = false;
	if (!el) return;

	function setWidth(width: number) {
		let maxWidth = window.innerWidth * 0.7;
		if (width < navigation.value.minWidth) {
			width = navigation.value.minWidth;
			localStorage.setItem("widthAlternis", String(navigation.value.minWidth));
		} else if (width > maxWidth) {
			width = maxWidth;
			localStorage.setItem("widthAlternis", String(maxWidth));
		} else {
			navigation.value.width = width;
			localStorage.setItem("widthAlternis", String(navigation.value.width));
		}
	}
	function resize(e: any) {
		e.preventDefault();
		e.stopPropagation();
		if (!el) return;
		document.body.style.cursor = "ew-resize";
		let f = e.clientX - 71;
		setWidth(f);
	}

	el.addEventListener(
		"pointerdown",
		function (e: any) {
			isDragged = true;
			if (e.offsetX < 2) {
				el.style.transition = "initial";
				document.addEventListener("pointermove", resize, false);
			}
		},
		false,
	);
	document.addEventListener(
		"pointerup",
		function (e: any) {
			if (!isDragged) return;
			el.style.transition = "";
			let f = e.clientX - 71;
			setWidth(f);
			document.body.style.cursor = "";
			document.removeEventListener("pointermove", resize, false);
			isDragged = false;
		},
		false,
	);
}

function getExpand(): boolean {
	const expandAlternis = localStorage.getItem("expandAlternis") || "true";
	return expandAlternis === "true";
}
function toggleExpand() {
	localStorage.setItem("expandAlternis", String(!isExpanded.value));
	alternisNewStore.isExpanded = !alternisNewStore.isExpanded;
	if (alternisNewStore.isExpanded) navigation.value.width = Math.max(getWidth(), navigation.value.minWidth);
	else navigation.value.width = navigation.value.minWidth;
}
function getWidth(): number {
	const widthAlternis = localStorage.getItem("widthAlternis") || navigation.value.minWidth;
	return Number(widthAlternis);
}

function hasPrefabPrefix(message: AlternisMessage): boolean {
	return message.text.length > 0 && message.text.startsWith("##");
}

function formatToolCall(event: ToolCallEvent) {
	const { toolType } = event.payload;

	switch (toolType) {
		case "set_attribute_value": {
			const { attributeId, value } = event.payload;
			const attribute = graphStore.instance.attribute.byId(attributeId);
			const conceptAttribute = graphStore.concept.attribute.byId(attribute?.concept || "");
			const attributeTerm =
				symbolsStore.term({
					id: conceptAttribute?.refersTo,
					terminology: conceptAttribute?.terminology,
					lang: domainStore.languagePreference,
				}) || String(attributeId);

			const enumItem = graphStore.descriptor.enumItem.byId(value);
			let valueTerm = "";
			if (enumItem) {
				valueTerm =
					symbolsStore.term({
						id: enumItem?.refersTo,
						terminology: enumItem?.terminology,
						lang: domainStore.languagePreference,
					}) || String(value);
			} else {
				valueTerm = String(value);
			}

			return `Set Attribute: <span style="color: rgb(var(--v-theme-primary))">${attributeTerm}</span> to <span style="color: rgb(var(--v-theme-primary))">${valueTerm}</span>`;
		}
		case "match_city": {
			const { cityQuery } = event.payload;
			return `Match City: <span style="color: rgb(var(--v-theme-primary))">${cityQuery}</span>`;
		}
		case "match_street": {
			const { streetQuery, city } = event.payload;
			return `Match Street: <span style="color: rgb(var(--v-theme-primary))">${streetQuery}</span> in <span style="color: rgb(var(--v-theme-primary))">${city}</span>`;
		}
		case "match_poi": {
			const { poiQuery, city } = event.payload;
			return `Match POI: <span style="color: rgb(var(--v-theme-primary))">${poiQuery}</span> in <span style="color: rgb(var(--v-theme-primary))">${city}</span>`;
		}
		case "match_poi_housenumber": {
			const { street, housenumber, city } = event.payload;
			return `Match Address: <span style="color: rgb(var(--v-theme-primary))">${street} ${housenumber}</span>, <span style="color: rgb(var(--v-theme-primary))">${city}</span>`;
		}
		case "get_pois_for_city": {
			const { city, placeTypes } = event.payload;
			return `Get POIs for City: <span style="color: rgb(var(--v-theme-primary))">${city}</span> (${placeTypes.join(
				", ",
			)})`;
		}
		case "confirm_suggestion": {
			const { attrName, value } = event.payload;
			return `Confirm Suggestion: <span style="color: rgb(var(--v-theme-primary))">${attrName}</span> = <span style="color: rgb(var(--v-theme-primary))">${value}</span>`;
		}
		case "end_session": {
			return `End Session`;
		}
		default:
			return `${toolType}`;
	}
}

onMounted(async () => {
	alternisNewStore.isExpanded = getExpand();
	if (isExpanded.value) navigation.value.width = getWidth();
	setEvents();
});

onUnmounted(() => {
	alternisNewStore.clear();
	alternisNewStore.closeSSE();
});
</script>

<style lang="scss">
#alternisborder {
	position: absolute;
	right: 0;
	top: 0;
	width: 2px;
	height: 100%;
	background-color: grey;
	z-index: 999;
	cursor: ew-resize;
	user-select: none;
}

.tool-call-chip-wrapper {
	display: flex;
	justify-content: center;
	padding: 8px 0;
}

.tool-call-chip {
	text-transform: none;
	font-weight: 500;
}
</style>
